import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import styled from 'styled-components';
import tw from 'twin.macro';
import { DateTime } from 'luxon';
import {
  GET_INVOICE_BY_ID,
  REFRESH_INVOICE_REFUND_STATUS,
  CREATE_REFUND_FOR_INVOICE,
} from '../../services/invoiceServices/queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSnapshot } from 'valtio';
import { useHostCapabilities } from '../../hooks/useHostCapabilities';
import { roleStore, Role } from '../../../store/roleStore';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import InvoiceDetailsSkeleton from '../../components/skeletons/InvoiceDetailsSkeleton';

const Container = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl p-6 box-border`}
`;

const Header = styled.div`
  ${tw`flex items-center mb-6`}
`;

const BackButton = styled(Link)`
  ${tw`flex items-center text-text-secondary mr-4`}

  &:hover {
    ${tw`text-text-primary`}
  }
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold`}
`;

const RefreshButton = styled.button`
  ${tw`ml-auto px-4 py-2 text-sm font-semibold rounded-md bg-black text-white hover:bg-black-hover disabled:opacity-50 disabled:cursor-not-allowed`}
`;

const CreateRefundButton = styled.button`
  ${tw`ml-2 px-4 py-2 text-sm font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed`}
`;

const RefundNoteWrapper = styled.div`
  ${tw`mt-4 flex flex-col gap-2`}
`;

const RefundNoteLabel = styled.label`
  ${tw`text-sm font-medium text-text-secondary`}
`;

const RefundNoteInput = styled.textarea`
  ${tw`w-full p-3 border border-border-default rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black`}
  min-height: 90px;
`;

const BookingLink = styled(Link)`
  ${tw`text-black hover:text-black font-semibold cursor-pointer`}
`;

interface DetailSectionProps {
  sectionType?: 'basic' | 'original' | 'new';
}

const DetailSection = styled.div<DetailSectionProps>`
  ${tw`mb-6 p-6 rounded-lg shadow-card`}
  background-color: ${props => {
    switch (props.sectionType) {
      case 'original':
        return '#FECACA'; // light red (red-200)
      case 'new':
        return '#A7F3D0'; // light green (green-200)
      case 'basic':
      default:
        return 'var(--color-bg-secondary)'; // light gray (gray-100)
    }
  }};
`;

const DetailTitle = styled.h3`
  ${tw`text-lg font-medium mb-4 pb-2 border-b border-border-default`}
`;

const DetailItem = styled.div`
  ${tw`flex flex-col md:flex-row md:justify-between md:items-center py-2 border-b border-border-default gap-1`}
`;

const Label = styled.span`
  ${tw`text-text-secondary`}
`;

const Value = styled.span`
  ${tw`font-medium break-all`}
`;

const BoldValue = styled.span`
  ${tw`font-bold break-all`}
`;

const RedBoldValue = styled.span`
  ${tw`font-bold text-red-600`}
`;

const GreenBoldValue = styled.span`
  ${tw`font-bold text-green-600`}
`;

interface StatusBadgeProps {
  status: string;
}

const StatusBadge = styled.span<StatusBadgeProps>`
  ${tw`px-2 py-1 text-xs font-semibold rounded-full`}
  background-color: ${props => {
    switch (props.status) {
      case 'PENDING_HOST_REVIEW':
        return '#FEF3C7'; // yellow-100
      case 'PROCESSING':
        return '#DBEAFE'; // blue-100
      case 'COMPLETED':
        return '#D1FAE5'; // green-100
      case 'FAILED':
        return '#FEE2E2'; // red-100
      case 'CANCELLED':
        return 'var(--color-bg-secondary)'; // gray-100
      default:
        return 'var(--color-bg-secondary)'; // gray-100
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'PENDING_HOST_REVIEW':
        return '#92400E'; // yellow-800
      case 'PROCESSING':
        return '#1E40AF'; // blue-800
      case 'COMPLETED':
        return '#065F46'; // green-800
      case 'FAILED':
        return '#991B1B'; // red-800
      case 'CANCELLED':
        return 'var(--color-text-secondary)'; // gray-800
      default:
        return 'var(--color-text-secondary)'; // gray-800
    }
  }};
`;

interface AmountDifferenceProps {
  amount: number;
}

const AmountDifference = styled.div<AmountDifferenceProps>`
  ${tw`text-xl font-bold mt-8 text-center p-4 rounded-lg`}
  background-color: ${props => (props.amount >= 0 ? '#ECFDF5' : '#FEF2F2')};
  color: ${props => (props.amount >= 0 ? '#047857' : '#DC2626')};
`;

export default function InvoiceDetailsPage() {
  const { id } = useParams();
  const { visible: canHost } = useHostCapabilities();
  const { role } = useSnapshot(roleStore);
  const location = useLocation();
  const [refundNote, setRefundNote] = useState('');

  // Get return status from navigation state
  const returnStatus = (location.state as { returnStatus?: string })?.returnStatus;
  const backUrl = returnStatus
    ? `/invoices/refund-status?status=${returnStatus}`
    : '/invoices/refund-status';

  const { data, loading, error, refetch } = useQuery(GET_INVOICE_BY_ID, {
    variables: { id },
    skip: !id,
  });

  const [refreshRefundStatus, { loading: refreshingStatus }] = useMutation(
    REFRESH_INVOICE_REFUND_STATUS,
    {
      onCompleted: () => {
        void refetch();
      },
      onError: error => {
        console.error('Failed to refresh refund status:', error.message);
      },
    },
  );

  const [createRefundForInvoice, { loading: creatingRefund }] = useMutation(
    CREATE_REFUND_FOR_INVOICE,
    {
      onCompleted: () => {
        void refetch();
      },
      onError: error => {
        const errorMessage = error.message || 'Unknown error occurred';
        alert(
          `Refund cannot be executed.\n\n${errorMessage}\n\nPlease check the Stripe account to see if you want to manually refund.\n\nClick OK to close this alert.`,
        );
      },
    },
  );

  const hasAutoRefreshed = useRef<string | null>(null);

  useEffect(() => {
    if (!data?.invoice?.id || hasAutoRefreshed.current === data.invoice.id) {
      return;
    }
    hasAutoRefreshed.current = data.invoice.id;
    refreshRefundStatus({ variables: { invoiceId: data.invoice.id } });
  }, [data?.invoice?.id, refreshRefundStatus]);

  const formatDate = (timestamp: number | undefined): string => {
    if (!timestamp) return 'N/A';
    return DateTime.fromMillis(timestamp).toFormat('MM/dd/yyyy');
  };

  if (loading) return <InvoiceDetailsSkeleton />;
  if (error)
    return (
      <Container>
        <p>Error: {error.message}</p>
      </Container>
    );
  if (!data?.invoice)
    return (
      <Container>
        <p>Invoice not found</p>
      </Container>
    );

  const invoice = data.invoice;
  const requesterEmail = invoice.requester?.email || invoice.requester?.displayName || 'N/A';
  const hostEmail = invoice.host?.email || invoice.host?.displayName || 'N/A';
  const cancellationReason = invoice.booking?.cancellationReason?.replace(/_/g, ' ');
  // Spec 014a: a brand may review refunds itself; its admins still can.
  const refundReviewAllowed = role === Role.ADMIN || canHost('refundReview');
  const canCreateRefund =
    refundReviewAllowed && !invoice.stripeRefundId && !!invoice.stripePaymentIntentId;

  return (
    <Container>
      <Header>
        <BackButton to={backUrl}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Invoices
        </BackButton>
        <Title>Invoice Details</Title>
        <RefreshButton
          onClick={() => {
            if (!invoice?.id) return;
            refreshRefundStatus({ variables: { invoiceId: invoice.id } });
          }}
          disabled={refreshingStatus}
        >
          {refreshingStatus ? 'Refreshing...' : 'Refresh Refund Status'}
        </RefreshButton>
        {canCreateRefund && (
          <CreateRefundButton
            onClick={() => {
              if (!invoice?.id) return;
              createRefundForInvoice({
                variables: {
                  invoiceId: invoice.id,
                  note: refundNote.trim() || undefined,
                },
              });
            }}
            disabled={creatingRefund}
          >
            {creatingRefund ? 'Creating...' : 'Create Refund Now'}
          </CreateRefundButton>
        )}
      </Header>

      <DetailSection sectionType="basic">
        <DetailTitle>Basic Information</DetailTitle>
        <DetailItem>
          <Label>Invoice ID:</Label>
          <Value>{invoice.id}</Value>
        </DetailItem>
        <DetailItem>
          <Label>Type:</Label>
          <Value>{invoice.type}</Value>
        </DetailItem>
        <DetailItem>
          <Label>Amount:</Label>
          <Value>${Number(invoice.amount).toFixed(2)}</Value>
        </DetailItem>
        <DetailItem>
          <Label>Invoice Status:</Label>
          <Value>{invoice.status}</Value>
        </DetailItem>
        <DetailItem>
          <Label>Created At:</Label>
          <Value>{DateTime.fromISO(invoice.createdAt).toFormat('MM/dd/yyyy HH:mm')}</Value>
        </DetailItem>
        <DetailItem>
          <Label>Approved At:</Label>
          <Value>
            {invoice.hostRefundReviewedAt
              ? DateTime.fromISO(invoice.hostRefundReviewedAt).toFormat('MM/dd/yyyy HH:mm')
              : 'N/A'}
          </Value>
        </DetailItem>
        {invoice.note ? (
          <DetailItem>
            <Label>Reason:</Label>
            <Value>{invoice.note || cancellationReason || 'N/A'}</Value>
          </DetailItem>
        ) : null}
        <DetailItem>
          <Label>Cancellation Reason:</Label>
          <Value>{cancellationReason || 'N/A'}</Value>
        </DetailItem>
        {canCreateRefund && (
          <RefundNoteWrapper>
            <RefundNoteLabel>Refund note (optional)</RefundNoteLabel>
            <RefundNoteInput
              value={refundNote}
              onChange={e => setRefundNote(e.target.value)}
              placeholder="Add a short note for the refund"
            />
          </RefundNoteWrapper>
        )}
        <DetailItem>
          <Label>Requester (Email):</Label>
          <Value>{requesterEmail}</Value>
        </DetailItem>
        <DetailItem>
          <Label>Host (Email):</Label>
          <Value>{hostEmail}</Value>
        </DetailItem>
        <DetailItem>
          <Label>Booking ID:</Label>
          <Value>
            {invoice.booking?.id ? (
              <BookingLink to={`/cars/host/booking/${invoice.booking.id}`}>
                {invoice.booking.id}
              </BookingLink>
            ) : (
              'N/A'
            )}
          </Value>
        </DetailItem>
        <DetailItem>
          <Label>Refund Status:</Label>
          <Value>
            <StatusBadge status={invoice.refundStatus}>{invoice.refundStatus}</StatusBadge>
          </Value>
        </DetailItem>
        {invoice.stripePaymentIntentId && (
          <DetailItem>
            <Label>Stripe Payment Intent ID:</Label>
            <Value>
              <a
                href={`https://dashboard.stripe.com/payments/${invoice.stripePaymentIntentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {invoice.stripePaymentIntentId}
              </a>
            </Value>
          </DetailItem>
        )}
        {invoice.stripeRefundId && (
          <DetailItem>
            <Label>Stripe Refund ID:</Label>
            <Value>
              <a
                href={`https://dashboard.stripe.com/refunds/${invoice.stripeRefundId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                {invoice.stripeRefundId}
              </a>
            </Value>
          </DetailItem>
        )}
      </DetailSection>

      {invoice.originalBookingState && (
        <DetailSection sectionType="original">
          <DetailTitle>Original Booking</DetailTitle>
          <DetailItem>
            <Label>Start Date:</Label>
            <Value>{formatDate(invoice.originalBookingState.startAt)}</Value>
          </DetailItem>
          <DetailItem>
            <Label>End Date:</Label>
            <Value>{formatDate(invoice.originalBookingState.endAt)}</Value>
          </DetailItem>
          {invoice.originalBookingState.carPricePerTrip && (
            <DetailItem>
              <Label>Rental Price:</Label>
              <Value>${invoice.originalBookingState.carPricePerTrip.toFixed(2)}</Value>
            </DetailItem>
          )}
          {invoice.originalBookingState.insuranceByTrip && (
            <DetailItem>
              <Label>Protection plan:</Label>
              <Value>${invoice.originalBookingState.insuranceByTrip.toFixed(2)}</Value>
            </DetailItem>
          )}
          <DetailItem>
            <Label>Total Price:</Label>
            <RedBoldValue>${invoice.originalBookingState.totalPrice.toFixed(2)}</RedBoldValue>
          </DetailItem>
        </DetailSection>
      )}

      {invoice.newBookingState && (
        <DetailSection sectionType="new">
          <DetailTitle>New Booking</DetailTitle>
          <DetailItem>
            <Label>Start Date:</Label>
            <Value>{formatDate(invoice.newBookingState.startAt)}</Value>
          </DetailItem>
          <DetailItem>
            <Label>End Date:</Label>
            <Value>{formatDate(invoice.newBookingState.endAt)}</Value>
          </DetailItem>
          {invoice.newBookingState.carPricePerTrip && (
            <DetailItem>
              <Label>Rental Price:</Label>
              <Value>${invoice.newBookingState.carPricePerTrip.toFixed(2)}</Value>
            </DetailItem>
          )}
          {invoice.newBookingState.insuranceByTrip && (
            <DetailItem>
              <Label>Protection plan:</Label>
              <Value>${invoice.newBookingState.insuranceByTrip.toFixed(2)}</Value>
            </DetailItem>
          )}
          <DetailItem>
            <Label>Total Price:</Label>
            <GreenBoldValue>${invoice.newBookingState.totalPrice.toFixed(2)}</GreenBoldValue>
          </DetailItem>
        </DetailSection>
      )}

      {invoice.originalBookingState && invoice.newBookingState && (
        <AmountDifference amount={Number(invoice.amount)}>
          Price Difference: ${Number(invoice.amount).toFixed(2)}
        </AmountDifference>
      )}
    </Container>
  );
}
